// ThreadRows — the rows the thread adds around its entries (pen "Chat – every
// entry" §9): a merged trail line, a day divider, and the "N waiting for
// you" pill. Pure apart from view-ephemeral open state (inside TrailLine).

import { exhaustSwitchCase } from "@bilda/utils";
import type { I18n } from "@lingui/core";
import { msg, plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Icon } from "../../Icon/Icon.js";
import {
    activityModel,
    activitySummary,
} from "../ToolActivityView/AgentActivityTrail.js";
import { trailGlyph } from "../ToolActivityView/ToolActivityView.js";
import { type TrailGlyph, TrailLine } from "../TrailLine/TrailLine.js";
import type { MergeableEntry, ThreadDay, TrailPart } from "../thread.js";
import { trailParts } from "../thread.js";
import type { AgentActivityEntry } from "../types.js";
import "./ThreadRows.css";

const lowerFirst = (s: string): string =>
    s.length === 0 ? s : s[0].toLowerCase() + s.slice(1);

// A run's activities as one: every step pooled under the first entry, so the
// summary names each kind of work once ("Read 2 sources", not "Read the
// source · read the source").
const pooled = (
    entries: readonly [AgentActivityEntry, ...AgentActivityEntry[]],
): AgentActivityEntry => ({
    ...entries[0],
    steps: entries.flatMap((entry) => entry.steps),
});

const partText = (part: TrailPart, inFlight: boolean, i18n: I18n): string => {
    switch (part.type) {
        case "activity":
            return activitySummary(
                activityModel(pooled(part.entries), inFlight, {}, i18n),
                i18n,
            );
        case "materials_made":
            return i18n._(
                msg`${plural(part.count, { one: "# material made", other: "# materials made" })}`,
            );
        case "exports_ready":
            return i18n._(
                msg`${plural(part.count, { one: "# export ready", other: "# exports ready" })}`,
            );
        case "answered":
            return i18n._(msg`Answered`);
        case "unknown":
            return i18n._(msg`An update this version cannot show yet`);
        default:
            return exhaustSwitchCase(part);
    }
};

const runGlyph = (
    parts: readonly TrailPart[],
    inFlight: boolean,
    i18n: I18n,
): TrailGlyph => {
    const glyphs = parts.flatMap((part) =>
        part.type === "activity"
            ? [
                  trailGlyph(
                      activityModel(pooled(part.entries), inFlight, {}, i18n)
                          .groups,
                  ),
              ]
            : [],
    );
    if (glyphs.includes("failed")) return "failed";
    if (glyphs.includes("running")) return "running";
    return "done";
};

// Trail lines in a row, as one line ("Read 3 sources · 1 material made");
// opening it shows each entry as it would stand alone.
export const TrailRun = ({
    entries,
    inFlight,
    renderEntry,
}: {
    readonly entries: readonly MergeableEntry[];
    readonly inFlight: boolean;
    readonly renderEntry: (entry: MergeableEntry) => ReactNode;
}): ReactNode => {
    const { i18n } = useLingui();
    const parts = trailParts(entries);
    const text = parts
        .map((part) => partText(part, inFlight, i18n))
        .filter((part) => part !== "")
        .map((part, i) => (i === 0 ? part : lowerFirst(part)))
        .join(" · ");
    return (
        <TrailLine glyph={runGlyph(parts, inFlight, i18n)} text={text}>
            {entries.map(renderEntry)}
        </TrailLine>
    );
};

const dayLabel = (day: ThreadDay, i18n: I18n): string => {
    switch (day.type) {
        case "today":
            return i18n._(msg`Today`);
        case "yesterday":
            return i18n._(msg`Yesterday`);
        case "earlier":
            return i18n.date(new Date(day.epochMs), {
                day: "numeric",
                month: "short",
            });
        default:
            return exhaustSwitchCase(day);
    }
};

// A quiet divider at the start of each day of a thread spanning days.
export const DayDivider = ({ day }: { readonly day: ThreadDay }): ReactNode => {
    const { i18n } = useLingui();
    return (
        <div className="bilda-thread-divider">
            <span className="bilda-thread-divider__label">
                {dayLabel(day, i18n)}
            </span>
        </div>
    );
};

// "New since you were here": once, where you left off. Neutral, not the
// accent: it orients, it does not alarm.
export const NewSinceDivider = (): ReactNode => {
    const { i18n } = useLingui();
    return (
        <div className="bilda-thread-divider bilda-thread-divider--new">
            <span className="bilda-thread-divider__label">
                {i18n._(msg`New since you were here`)}
            </span>
        </div>
    );
};

// "N waiting for you": shown at the top of the thread while a card that
// waits for you is out of sight; a click scrolls to it.
export const WaitingPill = ({
    count,
    onShow,
}: {
    readonly count: number;
    readonly onShow: () => void;
}): ReactNode => {
    const { i18n } = useLingui();
    return (
        <div className="bilda-thread-waiting">
            <button
                type="button"
                className="bilda-thread-waiting__pill"
                onClick={onShow}
            >
                {i18n._(msg`${count} waiting for you`)}
                <Icon name="arrow-up" className="bilda-thread-waiting__icon" />
            </button>
        </div>
    );
};
